import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import useColors from "../../api/hooks/useColors";

const ConfirmationDialog = ({
  openDialog,
  handleDialogClose,
  handleDialogCancel = null,
  handleConfirm,
  dialogTitle = "Confirmation",
  dialogContentFirstLine = "",
  dialogContentSecondLine = "",
  dialogContentThirdLine = "",
  extraContent = null,
  confirmationButtonEnabled = true,
  confirmationButtonText = "Confirm",
  cancelButtonText = "Cancel",
  destructive = false,
  sx = {},
  showCloseButton = true,
  isLoading = false,
  isLoadingText = "Deleting...",
}) => {
  const colors = useColors();
  return (
    <Dialog
      open={openDialog}
      onClose={handleDialogClose}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "10px",
        },
        ...sx,
      }}
    >
      <Box
        display={"flex"}
        flexDirection={"row"}
        gap={1}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <DialogTitle>
          <Typography variant="h6">{dialogTitle}</Typography>
        </DialogTitle>
        {showCloseButton && (
          <Box
            onClick={handleDialogClose}
            paddingRight={2}
            display={"flex"}
            justifyContent={"center"}
            sx={{ cursor: "pointer" }}
          >
            <ClearRoundedIcon
              sx={{
                width: "20px",
                height: "20px",
                color: colors.action_active,
              }}
            />
          </Box>
        )}
      </Box>
      <DialogContent sx={{ padding: 3, paddingTop: 0, paddingBottom: 2 }}>
        <DialogContentText sx={{ color: colors.text_secondary }}>
          {dialogContentFirstLine && (
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                lineHeight: 1.5,
                display: "block",
              }}
            >
              {dialogContentFirstLine}
            </Typography>
          )}
          {dialogContentSecondLine && (
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                lineHeight: 1.5,
                display: "block",
              }}
            >
              {dialogContentSecondLine}
            </Typography>
          )}
          {dialogContentThirdLine && (
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.5,
                display: "block",
              }}
            >
              {dialogContentThirdLine}
            </Typography>
          )}
          {extraContent && <Box sx={{ mt: 1 }}>{extraContent}</Box>}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleDialogCancel || handleDialogClose}
          sx={{
            textTransform: "none",
            color: destructive ? colors.text_primary : colors.primary_main,
          }}
          disabled={isLoading} // Disable cancel button during loading
        >
          <Typography
            variant="subtitle2"
            style={{ textTransform: "none", color: colors.text_secondary }}
          >
            {cancelButtonText}
          </Typography>
        </Button>
        <Button
          disabled={!confirmationButtonEnabled || isLoading} // Disable confirm button during loading
          onClick={handleConfirm}
          autoFocus
          sx={{
            color: destructive ? colors.error_main : colors.primary_main,
            textTransform: "none",
            "&.Mui-disabled": {
              color: colors.text_disabled,
            },
          }}
          startIcon={
            isLoading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          <Box gap={1} display={"flex"} flexDirection={"row"}>
            <Typography variant="subtitle2" style={{ textTransform: "none" }}>
              {isLoading ? isLoadingText : confirmationButtonText}
            </Typography>
            {!confirmationButtonEnabled && <CircularProgress size="1rem" />}
          </Box>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
